import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import OpportunityTable from '../components/opportunities/OpportunityTable';
import OpportunityModal from '../components/opportunities/OpportunityModal';
import Navbar from '../components/profile-components/Navbar';
import Topbar from '../components/profile-components/Topbar';
import AddOpportunityModal from '../components/opportunities/AddOpportunityModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import CreateTaskModal from '../components/Tasks/CreateTaskModal';
import TaskDetailModal from '../components/Tasks/TaskDetailModal';
import TaskList from '../components/Tasks/TaskList';
import Infobar from '../components/profile-components/Infobar';
import EmailComposer from '../components/profile-components/EmailComposer';
import ProfileMergeModal from '../components/profile-components/ProfileMergeModal';
import DocumentManager from '../components/documents/DocumentManager';
import SendSOAModal from '../components/SOA/SendSOAModal';
import SendACAConsentModal from '../components/ACAConsent/SendACAConsentModal';
import AddressVerificationFields from '../components/common/AddressVerificationFields';
import SpouseSelector from '../components/profile-components/SpouseSelector';
import './LeadProfile.css';
import './SalesPages.css';
import '../styles/Tabs.css';
import { authFetch } from '../utils/authFetch';
import usePageTitle from '../hooks/usePageTitle';
import CustomFieldsSection from '../components/custom-fields/CustomFieldsSection';

function LeadProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [lead, setLead] = useState({});
  const [activeTab, setActiveTab] = useState('Information');
  const [mobileTabsOpen, setMobileTabsOpen] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [sources, setSources] = useState([]);
  const [users, setUsers] = useState([]);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [recordHistory, setRecordHistory] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [products, setProducts] = useState([]);
  const [showOpportunityModal, setShowOpportunityModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSOAModal, setShowSOAModal] = useState(false);
  const [showACAModal, setShowACAModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [emailRecipient, setEmailRecipient] = useState(null);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [saving, setSaving] = useState(false);

  const [healthSherpaSettings, setHealthSherpaSettings] = useState({
    enabled: false,
    brokerUrl: '',
  });

  const mergeData = {
    first_name: lead.first_name,
    last_name: lead.last_name,
    full_name: lead.full_name || `${lead.first_name || ''} ${lead.last_name || ''}`.trim(),
  };

  const tabs = ['Information', 'Notes', 'Opportunities', 'Tasks', 'Call Logs', 'Record History'];

  const handleTabChange = (tab) => {
    setShowEmailComposer(false);
    setActiveTab(tab);
    setMobileTabsOpen(false);
  };

  const safeFetch = async (path, options = {}, label = 'Request') => {
    try {
      const res = await authFetch(path, options);
      const type = res.headers.get('content-type') || '';
      console.log(`📡 ${label} ->`, res.status, type);

      if (!res.ok) {
        const text = await res.text();
        console.error(`❌ ${label} failed (${res.status}):`, text.slice(0, 200));
        return null;
      }

      if (type.includes('application/json')) {
        return await res.json();
      }

      const text = await res.text();
      console.error(`⚠️ ${label} returned non-JSON:`, text.slice(0, 200));
      return null;
    } catch (err) {
      console.error(`💥 ${label} fetch error:`, err);
      return null;
    }
  };

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      setShowEmailComposer(false);
      setActiveTab(tab);
      setMobileTabsOpen(false);
    }
  }, [searchParams]);

  useEffect(() => {
    const oppId = searchParams.get('opportunityId');
    if (oppId && opportunities.length > 0) {
      const foundOpp = opportunities.find((o) => String(o.id) === String(oppId));
      if (foundOpp) {
        setSelectedOpportunity(foundOpp);
        setShowOpportunityModal(true);
      }
    }
  }, [opportunities, searchParams]);

  useEffect(() => {
    const taskId = searchParams.get('taskId');

    if (!taskId || tasks.length === 0) {
      return;
    }

    const foundTask = tasks.find((task) => String(task.id) === String(taskId));

    if (!foundTask) {
      return;
    }

    setShowEmailComposer(false);
    setActiveTab('Tasks');
    setMobileTabsOpen(false);
    setSelectedTask(foundTask);
    setShowTaskModal(true);

    const newParams = new URLSearchParams(searchParams);
    newParams.delete('taskId');
    setSearchParams(newParams, { replace: true });
  }, [tasks, searchParams, setSearchParams]);

  useEffect(() => {
    const loadProtected = async () => {
      const [leadData, notesData, historyData, oppsData, tasksData] = await Promise.all([
        safeFetch(`/api/leads/${id}`, {}, 'Lead fetch'),
        safeFetch(`/api/notes/${id}`, {}, 'Notes fetch'),
        safeFetch(`/api/record-history/${id}`, {}, 'Record history fetch'),
        safeFetch(`/api/opportunities/lead/${id}`, {}, 'Opportunities fetch'),
        safeFetch(`/api/tasks?relatedType=lead&relatedId=${id}`, {}, 'Tasks fetch'),
      ]);

      if (leadData) setLead(leadData);
      if (Array.isArray(notesData)) setNotes(notesData);
      if (Array.isArray(historyData)) setRecordHistory(historyData);
      if (Array.isArray(oppsData)) setOpportunities(oppsData);
      if (Array.isArray(tasksData)) setTasks(tasksData);

      if (leadData?.coverage_type_id && leadData?.carrier_id) {
        const prodData = await safeFetch(
          `/api/products?coverageTypeId=${leadData.coverage_type_id}&carrierId=${leadData.carrier_id}`,
          {},
          'Products fetch'
        );
        if (Array.isArray(prodData)) setProducts(prodData);
      }
    };

    loadProtected();
  }, [id]);

  useEffect(() => {
    const loadDropdowns = async () => {
      const [statusesData, sourcesData, usersData] = await Promise.all([
        safeFetch('/api/lead-statuses', {}, 'Lead statuses fetch'),
        safeFetch('/api/lead-sources', {}, 'Lead sources fetch'),
        safeFetch('/api/users', {}, 'Users fetch'),
      ]);

      if (Array.isArray(statusesData)) setStatuses(statusesData);
      if (Array.isArray(sourcesData)) setSources(sourcesData);
      if (Array.isArray(usersData)) setUsers(usersData);
    };

    loadDropdowns();
  }, []);

  useEffect(() => {
    const loadHealthSherpaSettings = async () => {
      const data = await safeFetch('/api/settings/healthsherpa', {}, 'HealthSherpa settings fetch');

      if (!data) return;

      setHealthSherpaSettings({
        enabled:
          data.healthsherpa_enabled === true ||
          data.healthsherpa_enabled === 1 ||
          data.healthsherpa_enabled === '1',
        brokerUrl: data.healthsherpa_broker_url || '',
      });
    };

    loadHealthSherpaSettings();
  }, []);

  const handleOpportunityClick = (opp) => {
    setSelectedOpportunity(null);
    setTimeout(() => {
      setSelectedOpportunity(opp);
      setShowOpportunityModal(true);
    }, 0);
  };

  const normalizeDate = (dateStr) => {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
  };

  const formatDate = (date) => (date ? new Date(date).toISOString().split('T')[0] : '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const leadToSave = {
      ...lead,
      dob: normalizeDate(lead.dob),
      part_a_date: normalizeDate(lead.part_a_date),
      part_b_date: normalizeDate(lead.part_b_date),
      address_verified: Number(lead.address_verified) ? 1 : 0,
    };

    const res = await safeFetch(
      `/api/leads/${id}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(leadToSave),
      },
      'Lead update'
    );

    if (res) alert('✅ Lead updated successfully!');
    setSaving(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setLead((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddressChange = (fieldName, value) => {
    setLead((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleOpenEmailComposer = (email) => {
    setEmailRecipient(email || lead.email || '');
    setShowEmailComposer(true);
    setMobileTabsOpen(false);
  };

  const handleCloseEmailComposer = () => {
    setShowEmailComposer(false);
    setEmailRecipient(null);
  };

  const handleQuoteACA = async () => {
    if (!healthSherpaSettings.enabled || !healthSherpaSettings.brokerUrl) {
      alert('HealthSherpa quoting is not enabled for your user account.');
      return;
    }

    const data = await safeFetch(
      `/api/healthsherpa/launch-link?recordType=lead&recordId=${id}`,
      {},
      'HealthSherpa launch link'
    );

    if (!data?.launch_url) {
      alert('Unable to open HealthSherpa quoting link.');
      return;
    }

    window.open(data.launch_url, '_blank', 'noopener,noreferrer');
  };

  const handleNoteSubmit = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    const res = await safeFetch(
      `/api/notes/${id}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: newNote }),
      },
      'Add note'
    );

    if (res) {
      setNotes([{ id: Date.now(), note: newNote, created_at: new Date().toISOString() }, ...notes]);
      setNewNote('');
    }
  };

  const handleDeleteLead = async () => {
    const res = await safeFetch(
      `/api/leads/${id}`,
      {
        method: 'DELETE',
      },
      'Delete lead'
    );

    if (res) {
      alert('Lead deleted');
      navigate('/leads');
    }
  };

  const handleConvertToClient = async () => {
    const confirmed = window.confirm(
      'Convert this lead to a client?\n\nThis will preserve notes, tasks, opportunities, record history, and documents.'
    );
    if (!confirmed) return;

    const res = await safeFetch(
      `/api/leads/${id}/convert`,
      {
        method: 'POST',
      },
      'Convert lead to client'
    );

    if (res?.clientId) {
      alert('✅ Lead converted to client.');
      navigate(`/client-profile/${res.clientId}?createOpportunity=true`);
    } else {
      alert('❌ Convert failed. Check server logs.');
    }
  };

  const handleOpportunitySubmit = async (formData) => {
    const payload = { ...formData, lead_id: id };
    const res = await safeFetch(
      '/api/opportunities',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
      'Add opportunity'
    );

    if (res) {
      setOpportunities((prev) => [...prev, res]);
      setShowOpportunityModal(false);
      return res;
    }

    return null;
  };

  const handleUpdateOpportunity = async (updatedOpp) => {
    const res = await safeFetch(
      `/api/opportunities/${updatedOpp.id}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedOpp),
      },
      'Update opportunity'
    );

    if (res) {
      setOpportunities((prev) => prev.map((o) => (o.id === res.id ? res : o)));
      setSelectedOpportunity(res);

      if (!res.shouldPromptConversion) {
        setSelectedOpportunity(null);
        setShowOpportunityModal(false);
      }

      return res;
    }

    return null;
  };

  const handleDeleteOpportunity = async (idToDelete) => {
    const res = await safeFetch(
      `/api/opportunities/${idToDelete}`,
      {
        method: 'DELETE',
      },
      'Delete opportunity'
    );

    if (res !== null) {
      setOpportunities(opportunities.filter((o) => o.id !== idToDelete));
      setSelectedOpportunity(null);
    }
  };

  const handleAddTask = () => {
    setSelectedTask(null);
    setShowCreateTaskModal(true);
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setShowTaskModal(true);
  };

  const fullName =
    `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Lead Profile';

  usePageTitle(fullName);

  const statusName = useMemo(() => {
    const match = statuses.find((s) => String(s.id) === String(lead.lead_status_id));
    return match?.name || '';
  }, [statuses, lead.lead_status_id]);

  const sourceName = useMemo(() => {
    const match = sources.find((s) => String(s.id) === String(lead.lead_source_id));
    return match?.name || '';
  }, [sources, lead.lead_source_id]);

  const aorName = useMemo(() => {
    const match = users.find((u) => String(u.id) === String(lead.aor_user_id));
    return match?.name || '';
  }, [users, lead.aor_user_id]);

  const canQuoteACA = Boolean(
    healthSherpaSettings.enabled && healthSherpaSettings.brokerUrl
  );

  const statusChipClass = (value) => {
    const normalized = String(value || 'unknown').toLowerCase().replace(/\s+/g, '_');
    return `sales-chip ${normalized}`;
  };

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="sales-hero sales-profile-hero">
              <div>
                <p className="sales-eyebrow">Agent Book CRM</p>
                <h1 className="sales-title">{fullName}</h1>
                <p className="sales-subtitle">
                  Manage lead details, communications, opportunities, tasks, and record activity.
                </p>

                <div className="sales-hero-meta">
                  <span className={statusChipClass(statusName || 'pending')}>
                    {statusName || 'No Status'}
                  </span>

                  {sourceName && (
                    <span className="sales-chip sales-chip-neutral">{sourceName}</span>
                  )}

                  {aorName && (
                    <span className="sales-chip sales-chip-outline">AOR: {aorName}</span>
                  )}
                </div>
              </div>

              <div className="sales-header-actions">
                <button type="button" className="sales-primary-btn" onClick={handleConvertToClient}>
                  Convert to Client
                </button>

                <button type="button" className="sales-secondary-btn" onClick={handleAddTask}>
                  Add Task
                </button>

                <button type="button" className="sales-secondary-btn" onClick={() => setShowSOAModal(true)}>
                  Send SOA
                </button>

                <button type="button" className="sales-secondary-btn" onClick={() => setShowACAModal(true)}>
                  Send ACA
                </button>

                {canQuoteACA && (
                  <button type="button" className="sales-secondary-btn" onClick={handleQuoteACA}>
                    Quote ACA
                  </button>
                )}

                <button
                  type="button"
                  className="sales-secondary-btn"
                  onClick={() => handleOpenEmailComposer(lead.email)}
                >
                  Send Email
                </button>

                <button
                  type="submit"
                  form="lead-profile-form"
                  className="sales-secondary-btn"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>

            <div className="profile-mobile-tab-menu">
              <button
                type="button"
                className="profile-mobile-tab-button"
                onClick={() => setMobileTabsOpen((prev) => !prev)}
              >
                <span>{activeTab}</span>
                <span className="profile-mobile-tab-icon">
                  {mobileTabsOpen ? '▲' : '▼'}
                </span>
              </button>

              {mobileTabsOpen && (
                <div className="profile-mobile-tab-options">
                  {tabs.map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      className={activeTab === tab ? 'active' : ''}
                      onClick={() => handleTabChange(tab)}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="tabs profile-desktop-tabs">
              {tabs.map((tab) => (
                <div
                  key={tab}
                  className={`tab ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab)}
                >
                  {tab}
                </div>
              ))}
            </div>

            <div className="tab-content">
              {showEmailComposer ? (
                <EmailComposer
                  recipientEmail={emailRecipient || lead.email || ''}
                  recipientName={fullName}
                  leadId={lead.id}
                  mergeData={mergeData}
                  onClose={handleCloseEmailComposer}
                />
              ) : (
                <>
                  {activeTab === 'Information' && (
                    <form id="lead-profile-form" onSubmit={handleSubmit}>
                      <div className="sales-profile-grid">
                        <div className="sales-section-card sales-section-lead">
                          <div className="sales-section-head">
                            <h2>Lead Information</h2>
                            <p>Status, source, ownership, personal details, and Medicare fields.</p>
                          </div>

                          <div className="sales-form-fields">
                            <div className="sales-field">
                              <label>Status</label>
                              <select name="lead_status_id" value={lead.lead_status_id || ''} onChange={handleChange}>
                                <option value="">Select</option>
                                {statuses.map((status) => (
                                  <option key={status.id} value={status.id}>{status.name}</option>
                                ))}
                              </select>
                            </div>

                            <div className="sales-field">
                              <label>Source</label>
                              <select name="lead_source_id" value={lead.lead_source_id || ''} onChange={handleChange}>
                                <option value="">Select</option>
                                {sources.map((source) => (
                                  <option key={source.id} value={source.id}>{source.name}</option>
                                ))}
                              </select>
                            </div>

                            <div className="sales-field">
                              <label>Referred By</label>
                              <input name="referred_by" value={lead.referred_by || ''} onChange={handleChange} />
                            </div>

                            <div className="sales-field">
                              <label>AOR</label>
                              <select name="aor_user_id" value={lead.aor_user_id || ''} onChange={handleChange}>
                                <option value="">Select</option>
                                {users.map((user) => (
                                  <option key={user.id} value={user.id}>{user.name}</option>
                                ))}
                              </select>
                            </div>

                            <div className="sales-field">
                              <label>First Name</label>
                              <input name="first_name" value={lead.first_name || ''} onChange={handleChange} />
                            </div>

                            <div className="sales-field">
                              <label>Last Name</label>
                              <input name="last_name" value={lead.last_name || ''} onChange={handleChange} />
                            </div>

                            <div className="sales-field">
                              <label>Marital Status</label>
                              <select name="marital_status" value={lead.marital_status || ''} onChange={handleChange}>
                                <option value="">Select</option>
                                <option value="Single">Single</option>
                                <option value="Married">Married</option>
                                <option value="Separated">Separated</option>
                              </select>
                            </div>

                            <div className="sales-field">
                              <label>Spouse</label>
                              <SpouseSelector
                                recordType="lead"
                                record={lead}
                                onLinked={(spouseData) => {
                                  setLead((prev) => ({
                                    ...prev,
                                    ...spouseData
                                  }));
                                }}
                                onUnlinked={() => {
                                  setLead((prev) => ({
                                    ...prev,
                                    spouse_record_type: null,
                                    spouse_record_id: null,
                                    spouse_name: ''
                                  }));
                                }}
                              />
                            </div>

                            <div className="sales-field">
                              <label>Date of Birth</label>
                              <input name="dob" type="date" value={formatDate(lead.dob)} onChange={handleChange} />
                            </div>

                            <div className="sales-field">
                              <label>Gender</label>
                              <select name="gender" value={lead.gender || ''} onChange={handleChange}>
                                <option value="">Select</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Other">Other</option>
                              </select>
                            </div>

                            <div className="sales-field">
                              <label>Medicaid Number</label>
                              <input name="medicaid_number" value={lead.medicaid_number || ''} onChange={handleChange} />
                            </div>

                            <div className="sales-field">
                              <label>Medicare Number</label>
                              <input name="medicare_number" value={lead.medicare_number || ''} onChange={handleChange} />
                            </div>

                            <div className="sales-field">
                              <label>Part A Date</label>
                              <input name="part_a_date" type="date" value={formatDate(lead.part_a_date)} onChange={handleChange} />
                            </div>

                            <div className="sales-field">
                              <label>Part B Date</label>
                              <input name="part_b_date" type="date" value={formatDate(lead.part_b_date)} onChange={handleChange} />
                            </div>

                            <CustomFieldsSection
                              recordType="lead"
                              recordId={lead.id}
                            />
                          </div>
                        </div>

                        <div className="sales-section-card sales-section-subscription">
                          <div className="sales-section-head">
                            <h2>Contact Details</h2>
                            <p>Address and contact information for this lead.</p>
                          </div>

                          <div className="sales-form-fields">
                            <div className="sales-field">
                              <label>Phone</label>
                              <input name="phone" value={lead.phone || ''} onChange={handleChange} />
                            </div>

                            <div className="sales-field">
                              <label>Email</label>
                              <input name="email" value={lead.email || ''} onChange={handleChange} />
                            </div>

                            <AddressVerificationFields
                              values={lead}
                              onChange={handleAddressChange}
                              fieldMap={{
                                street: 'street_address',
                                unit: 'unit',
                                city: 'city',
                                state: 'state',
                                zip: 'zip_code',
                                county: 'county',
                                addressVerified: 'address_verified',
                              }}
                            />

                            <input
                              type="hidden"
                              name="address_verified"
                              value={lead.address_verified || 0}
                            />
                          </div>
                        </div>
                      </div>
                    </form>
                  )}

                  {activeTab === 'Notes' && (
                    <div className="sales-section-card sales-section-lead">
                      <div className="sales-section-head" style={{ marginBottom: '14px' }}>
                        <h2>Notes</h2>
                        <p>Capture context, follow-ups, and communication history for this lead.</p>
                      </div>

                      <form onSubmit={handleNoteSubmit} style={{ marginBottom: '18px' }}>
                        <div className="sales-field">
                          <label>Add Note</label>
                          <textarea
                            className="sales-note-textarea"
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            placeholder="Add a concise note, follow-up detail, or internal comment..."
                          />
                        </div>

                        <div style={{ marginTop: '12px' }}>
                          <button type="submit" className="sales-primary-btn">
                            Add Note
                          </button>
                        </div>
                      </form>

                      {notes.length > 0 ? (
                        <div className="profile-note-list">
                          {notes.map((note) => {
                            const isEmail = note.content?.startsWith('📧 Email Sent');

                            return (
                              <div key={note.id} className="profile-note-card">
                                <div className="profile-note-card-header">
                                  <div className="profile-note-type">
                                    {isEmail ? 'Email Activity' : 'Note'}
                                  </div>

                                  <div className="profile-note-date">
                                    {new Date(note.created_at).toLocaleString()}
                                  </div>
                                </div>

                                {isEmail ? (
                                  <div
                                    className="profile-note-email-content"
                                    dangerouslySetInnerHTML={{ __html: note.content }}
                                  />
                                ) : (
                                  <div className="profile-note-body">
                                    {note.note}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="profile-empty-card">
                          No notes available for this lead.
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'Opportunities' && (
                    <div className="sales-table-card">
                      <div className="sales-table-header">
                        <div>
                          <h2>Opportunities</h2>
                          <p>{opportunities.length} opportunit{opportunities.length === 1 ? 'y' : 'ies'}</p>
                        </div>

                        <button
                          type="button"
                          className="sales-primary-btn"
                          onClick={() => {
                            setSelectedOpportunity(null);
                            setShowOpportunityModal(true);
                          }}
                        >
                          + Add Opportunity
                        </button>
                      </div>

                      <div className="sales-table-wrap">
                        <OpportunityTable opportunities={opportunities} onEdit={handleOpportunityClick} />
                      </div>
                    </div>
                  )}

                  {activeTab === 'Tasks' && (
                    <div className="sales-table-card">
                      <div className="sales-table-header">
                        <div>
                          <h2>Tasks</h2>
                          <p>{tasks.length} task{tasks.length === 1 ? '' : 's'}</p>
                        </div>

                        <button type="button" className="sales-primary-btn" onClick={handleAddTask}>
                          + Add Task
                        </button>
                      </div>

                      <div className="sales-table-wrap">
                        <TaskList
                          relatedId={lead.id}
                          relatedType="lead"
                          onTaskClick={handleTaskClick}
                        />
                      </div>
                    </div>
                  )}

                  {activeTab === 'Call Logs' && (
                    <div className="sales-section-card">
                      <div className="sales-section-head">
                        <h2>Call Logs</h2>
                        <p>Call logging UI can be added here without changing the tab structure.</p>
                      </div>

                      <p style={{ margin: 0, color: '#64748b' }}>
                        No call logs available yet.
                      </p>
                    </div>
                  )}

                  {activeTab === 'Record History' && (
                    <div className="sales-profile-grid">
                      <div className="sales-section-card sales-section-lead">
                        <div className="sales-section-head" style={{ marginBottom: '14px' }}>
                          <h2>Record History</h2>
                          <p>A chronological timeline of important actions and activity on this record.</p>
                        </div>

                        {recordHistory.length > 0 ? (
                          <div className="profile-activity-list">
                            {recordHistory.map((r) => (
                              <div key={r.id} className="profile-activity-card">
                                <div className="profile-activity-accent" />

                                <div className="profile-activity-type">
                                  Activity
                                </div>

                                <div className="profile-activity-body">
                                  {r.action}
                                </div>

                                <div className="profile-activity-date">
                                  {new Date(r.created_at).toLocaleString()}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="profile-empty-card">
                            No record history available.
                          </div>
                        )}
                      </div>

                      <div className="sales-section-card sales-section-subscription">
                        <div className="sales-section-head" style={{ marginBottom: '14px' }}>
                          <h2>Documents</h2>
                          <p>Stored files and generated documents associated with this lead.</p>
                        </div>

                        <DocumentManager recordId={id} recordType="lead" />

                          <div style={{ marginTop: '18px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              className="sales-secondary-btn"
                              onClick={() => setShowMergeModal(true)}
                              disabled={!lead.id}
                            >
                              Merge Profile
                            </button>

                            <button
                              type="button"
                              onClick={() => setShowDeleteModal(true)}
                              className="sales-danger-btn"
                            >
                              Delete Profile
                            </button>
                          </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {showOpportunityModal &&
              (selectedOpportunity ? (
                <OpportunityModal
                  opportunity={selectedOpportunity}
                  products={products}
                  onSave={handleUpdateOpportunity}
                  onDelete={handleDeleteOpportunity}
                  onClose={() => setShowOpportunityModal(false)}
                />
              ) : (
                <AddOpportunityModal
                  products={products}
                  leadId={id}
                  onSubmit={handleOpportunitySubmit}
                  onClose={() => setShowOpportunityModal(false)}
                />
              ))}

            {showSOAModal && (
              <SendSOAModal lead={lead} onClose={() => setShowSOAModal(false)} />
            )}

            {showACAModal && (
              <SendACAConsentModal
                record={lead}
                recordType="lead"
                onClose={() => setShowACAModal(false)}
              />
            )}

            {showTaskModal && selectedTask && (
              <TaskDetailModal
                task={selectedTask}
                onClose={() => {
                  setSelectedTask(null);
                  setShowTaskModal(false);
                }}
              />
            )}

            {lead?.id && showCreateTaskModal && (
              <CreateTaskModal
                onClose={() => setShowCreateTaskModal(false)}
                relatedId={lead.id}
                relatedType="lead"
                onCreated={() => {
                  setShowCreateTaskModal(false);
                }}
              />
            )}

            {showDeleteModal && (
              <DeleteConfirmationModal
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteLead}
                confirmText={confirmText}
                setConfirmText={setConfirmText}
              />
            )}

            {lead.id && showMergeModal && (
              <ProfileMergeModal
                recordType="lead"
                primaryId={lead.id}
                primaryName={fullName}
                onClose={() => setShowMergeModal(false)}
                onMerged={() => window.location.reload()}
              />
            )}
          </div>

          <Infobar
            record={lead}
            relatedId={lead.id}
            relatedType="lead"
            onEmailClick={handleOpenEmailComposer}
            onOpenPolicyOrOppModal={(item) => {
              setSelectedOpportunity(item);
              setShowOpportunityModal(true);
              handleTabChange('Opportunities');
            }}
          />
        </div>
      </div>
    </>
  );
}

export default LeadProfile;